import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import type { NavigationDomain, NavigationOrigin, NavigationEntry } from '@/composables/navigation/types';
import { TOP_LEVEL_ROUTES, TOP_LEVEL_ROUTE_MAP } from '@/composables/navigation/types';

export interface DomainState {
  entries: NavigationEntry[];
  currentIndex: number;
  origin: NavigationOrigin;
}

export interface DomainStates {
  location?: {
    selectedLocationId?: string;
    scrollPosition?: number;
  };
  booking?: {
    bookingContext?: Record<string, any>;
  };
  chat?: {
    activeConversationId?: string;
    scrollPosition?: number;
  };
  profile?: {
    activeTab?: string;
    scrollPosition?: number;
  };
  search?: {
    searchQuery?: string;
    activeFilters?: Record<string, any>;
    scrollPosition?: number;
  };
  auth?: {
    authView?: string;
    phoneData?: {
      phoneNumber: string;
      countryCode: string;
      country: string;
    };
  };
}

export interface PersistedNavigationState {
  version: number;
  timestamp: number;
  activeDomain: NavigationDomain | null;
  stacks: Record<NavigationDomain, DomainState>;
  domainStates: DomainStates;
}

const STORAGE_KEY = 'navigationState';
const CURRENT_VERSION = 1;

export const useDomainStateStore = defineStore('domainState', () => {
  const activeDomain = ref<NavigationDomain | null>(null);
  const stacks = ref<Record<NavigationDomain, DomainState>>({
    foryou: { entries: [], currentIndex: -1, origin: null },
    search: { entries: [], currentIndex: -1, origin: null },
    chat: { entries: [], currentIndex: -1, origin: null },
    profile: { entries: [], currentIndex: -1, origin: null },
    settings: { entries: [], currentIndex: -1, origin: null },
    location: { entries: [], currentIndex: -1, origin: null },
    booking: { entries: [], currentIndex: -1, origin: null },
    creator: { entries: [], currentIndex: -1, origin: null },
    bookingHistory: { entries: [], currentIndex: -1, origin: null },
    auth: { entries: [], currentIndex: -1, origin: null }
  });
  const domainStates = ref<DomainStates>({});

  const saveToStorage = () => {
    try {
      const data: PersistedNavigationState = {
        version: CURRENT_VERSION,
        timestamp: Date.now(),
        activeDomain: activeDomain.value,
        stacks: JSON.parse(JSON.stringify(stacks.value)),
        domainStates: JSON.parse(JSON.stringify(domainStates.value))
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to save navigation state:', e);
    }
  };

  const restoreFromStorage = (): boolean => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return false;

      const data: PersistedNavigationState = JSON.parse(saved);
      
      if (data.version !== CURRENT_VERSION) {
        console.warn('Navigation state version mismatch, resetting');
        return false;
      }

      if (data.timestamp && Date.now() - data.timestamp > 7 * 24 * 60 * 60 * 1000) {
        console.warn('Navigation state too old, resetting');
        return false;
      }

      activeDomain.value = data.activeDomain ?? null;
      stacks.value = data.stacks;
      domainStates.value = data.domainStates ?? {};
      
      return true;
    } catch (e) {
      console.warn('Failed to restore navigation state:', e);
      return false;
    }
  };

  const clearAllStacks = () => {
    const domainKeys = Object.keys(stacks.value) as NavigationDomain[];
    domainKeys.forEach(domain => {
      stacks.value[domain] = { entries: [], currentIndex: -1, origin: null };
    });
    activeDomain.value = null;
    saveToStorage();
  };

  const clearStack = (domain: NavigationDomain) => {
    stacks.value[domain] = { entries: [], currentIndex: -1, origin: null };
    saveToStorage();
  };

  const updateStack = (domain: NavigationDomain, state: DomainState) => {
    stacks.value[domain] = { ...state };
    activeDomain.value = domain;
    saveToStorage();
  };

  const getStack = (domain: NavigationDomain): DomainState => {
    return stacks.value[domain];
  };

  const setActiveDomain = (domain: NavigationDomain | null) => {
    activeDomain.value = domain;
    saveToStorage();
  };

  const updateDomainState = <K extends keyof DomainStates>(
    domain: K,
    state: Partial<DomainStates[K]>
  ) => {
    if (!domainStates.value[domain]) {
      domainStates.value[domain] = {} as DomainStates[K];
    }
    domainStates.value[domain] = {
      ...domainStates.value[domain],
      ...state
    };
    saveToStorage();
  };

  const getDomainState = <K extends keyof DomainStates>(domain: K): DomainStates[K] | undefined => {
    return domainStates.value[domain] as DomainStates[K] | undefined;
  };

  const isTopLevelRoute = (path: string): boolean => {
    return TOP_LEVEL_ROUTES.includes(path as typeof TOP_LEVEL_ROUTES[number]);
  };

  const getTopLevelOrigin = (path: string): NavigationOrigin | null => {
    return TOP_LEVEL_ROUTE_MAP[path] || null;
  };

  const init = () => {
    restoreFromStorage();
  };

  return {
    activeDomain,
    stacks,
    domainStates,
    saveToStorage,
    restoreFromStorage,
    clearAllStacks,
    clearStack,
    updateStack,
    getStack,
    setActiveDomain,
    updateDomainState,
    getDomainState,
    isTopLevelRoute,
    getTopLevelOrigin,
    init
  };
});